import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductDetails, clearProductDetails } from '../redux/menu/ProductSlice';
import { addCart, removeOrder } from '../redux/menu/cartSlice';
import { toggleFavorite } from '../redux/menu/favoritesSlice';
import { FaCheckCircle, FaCog, FaChevronLeft, FaChevronRight, FaShareAlt, FaMapMarkerAlt, FaWhatsapp, FaPlay, FaGasPump, FaIdCard } from 'react-icons/fa';
import { HiOutlineHeart, HiHeart } from 'react-icons/hi';
import useTheme from '../hooks/useTheme';
import Back from '../components/Common/Back';

const ProductDetailsScreen = () => {
  const { id, productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const { productDetails, productDetailsStatus, error } = useSelector((state) => state.product);
  const { profile } = useSelector((state) => state.profile);
  const cartItems = useSelector((state) => state.cart.cart);
  const favorites = useSelector((state) => state.favorites.favorites);

  const [selectedMainImage, setSelectedMainImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showShareToast, setShowShareToast] = useState(false);
  const [activeTab, setActiveTab] = useState('image'); // 'image' or 'video'
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoTouchStart, setVideoTouchStart] = useState(null);
  const [videoTouchEnd, setVideoTouchEnd] = useState(null);
  const imagePreviewRef = useRef(null);
  const videoPreviewRef = useRef(null);

  // Check if product is favorited
  const isFavorited = favorites.some(fav => fav.id === productDetails?.id);

  // Handle favorite toggle
  const handleFavoriteToggle = () => {
    if (productDetails) {
      // Format product data for favorites screen
      const favoriteProduct = {
        ...productDetails,
        image: productDetails?.DocImage?.imageUrl || productDetails?.image,
        name: productDetails?.name,
        price: productDetails?.price,
        currency: profile?.currency || "AED",
        description: productDetails?.description,
        categoryName: productDetails?.ProductCategory?.name,
        subcategoryName: productDetails?.ProductSubCategory?.name,
        brand: productDetails?.brand,
        model: productDetails?.model,
        warranty: productDetails?.warranty,
        availability: productDetails?.availability,
        map_url: productDetails?.map_url,
        ProductSpecifications: productDetails?.ProductSpecifications,
        ProductFeatures: productDetails?.ProductFeatures
      };
      dispatch(toggleFavorite(favoriteProduct));
    }
  };

  // Reorder variants so that any variant named "Special" appears at the end
  // Only include variants that have sub-variants
  const reorderedVariants = useMemo(() => {
    const variants = productDetails?.ProductVariants || [];
    const colorVariants = [];
    const normalVariants = [];
    const specialVariants = [];
    variants.forEach(v => {
      // Only include variants that have sub-variants
      if (v.ProductSubVariants && v.ProductSubVariants.length > 0) {
        const name = typeof v?.name === 'string' ? v.name.trim().toLowerCase() : '';
        if (name === 'special') {
          specialVariants.push(v);
        } else if (name === 'color' || name === 'colour') {
          colorVariants.push(v);
        } else {
          normalVariants.push(v);
        }
      }
    });
    return [...colorVariants, ...normalVariants, ...specialVariants];
  }, [productDetails?.ProductVariants]);

  // Map for quick variant lookup by id
  const variantIdToVariant = useMemo(() => {
    const map = {};
    (productDetails?.ProductVariants || []).forEach(v => {
      map[v.id] = v;
    });
    return map;
  }, [productDetails?.ProductVariants]);

  // Whether a Special variant is currently selected
  const isSpecialSelected = useMemo(() => {
    return Object.entries(selectedVariants).some(([vId, _svId]) => {
      const name = typeof variantIdToVariant[vId]?.name === 'string' ? variantIdToVariant[vId].name.trim().toLowerCase() : '';
      return name === 'special';
    });
  }, [selectedVariants, variantIdToVariant]);

  

  useEffect(() => {
    if (productId) {
      dispatch(fetchProductDetails({ productId }));
    }

    return () => {
      dispatch(clearProductDetails());
    };
  }, [productId, dispatch]);

  // Set main image and auto-select first variants when product details load
  useEffect(() => {
    const images = getAllImages();
    if (images.length > 0) {
      setSelectedMainImage(images[0]);
      setCurrentImageIndex(0);
    }

    // Auto-select first variant option for each variant
    if (productDetails?.ProductVariants && productDetails.ProductVariants.length > 0) {
      const initialVariants = {};
      reorderedVariants.forEach(variant => {
        const name = typeof variant?.name === 'string' ? variant.name.trim().toLowerCase() : '';
        // Do not auto-select Special variant
        if (name === 'special') return;
        if (variant.ProductSubVariants && variant.ProductSubVariants.length > 0) {
          initialVariants[variant.id] = variant.ProductSubVariants[0].id;
        }
      });
      setSelectedVariants(initialVariants);
    }
  }, [productDetails]);

  const handleAddToCart = () => {
    // For products with variants, create a unique cart ID based on selected variants
    let cartId = productDetails.id;
    let variantDisplayName = '';
    
    if (hasVariants && productDetails.ProductVariants) {
      const variantNames = Object.entries(selectedVariants)
        .map(([variantId, subVariantId]) => {
          const variant = productDetails.ProductVariants.find(v => v.id === variantId);
          const subVar = variant?.ProductSubVariants?.find(sv => sv.id === subVariantId);
          return subVar?.name;
        })
        .filter(Boolean);
      
      if (variantNames.length > 0) {
        variantDisplayName = ` (${variantNames.join(', ')})`;
        // Create unique ID based on selected variants
        cartId = `${productDetails.id}-${Object.values(selectedVariants).join('-')}`;
      }
    }

    const totalPrice = calculateTotalPrice();
    const existingItem = cartItems.find(item => item.id === cartId);
    const currentQuantity = existingItem?.quantity || 0;

    const cartItem = {
      id: cartId,
      name: `${productDetails.name}${variantDisplayName}`,
      price: totalPrice,
      originalPrice: parseFloat(productDetails.price),
      offer: productDetails?.offer || null,
      quantity: currentQuantity + 1,
      image: productDetails?.DocImage?.imageUrl,
      currency: profile?.currency || "AED",
      is_veg: productDetails?.is_veg,
      selectedVariants: hasVariants ? selectedVariants : null,
      brand: productDetails?.brand,
      model: productDetails?.model,
      warranty: productDetails?.warranty
    };

    dispatch(addCart(cartItem));
    
    // Navigate back to home screen
    navigate(`/${id}`);
  };

  const getCartQuantity = () => {
    let cartId = productDetails?.id;
    
    if (hasVariants && productDetails.ProductVariants) {
      const variantIds = Object.values(selectedVariants);
      if (variantIds.length > 0) {
        cartId = `${productDetails.id}-${variantIds.join('-')}`;
      }
    }
    
    const item = cartItems.find(item => item.id === cartId);
    return item?.quantity || 0;
  };

  const updateQuantity = (newQuantity) => {
    let cartId = productDetails.id;
    let variantDisplayName = '';
    
    if (hasVariants && productDetails.ProductVariants) {
      const variantNames = Object.entries(selectedVariants)
        .map(([variantId, subVariantId]) => {
          const variant = productDetails.ProductVariants.find(v => v.id === variantId);
          const subVar = variant?.ProductSubVariants?.find(sv => sv.id === subVariantId);
          return subVar?.name;
        })
        .filter(Boolean);
      
      if (variantNames.length > 0) {
        variantDisplayName = ` (${variantNames.join(', ')})`;
        cartId = `${productDetails.id}-${Object.values(selectedVariants).join('-')}`;
      }
    }

    if (newQuantity === 0) {
      dispatch(removeOrder(cartId));
    } else {
      const totalPrice = calculateTotalPrice();

      const cartItem = {
        id: cartId,
        name: `${productDetails.name}${variantDisplayName}`,
        price: totalPrice,
        originalPrice: parseFloat(productDetails.price),
        offer: productDetails?.offer || null,
        quantity: newQuantity,
        image: productDetails?.DocImage?.imageUrl,
        currency: profile?.currency || "AED",
        is_veg: productDetails?.is_veg,
        selectedVariants: hasVariants ? selectedVariants : null
      };

      dispatch(addCart(cartItem));
    }
  };

  // Map handler function
  const handleMapClick = () => {
    if (productDetails?.map_url) {
      // Open map URL in new tab
      window.open(productDetails.map_url, '_blank', 'noopener,noreferrer');
    }
  };

  // WhatsApp enquiry handler
  const handleWhatsAppEnquiry = () => {
    if (!profile?.whatsapp) return;
    
    const companyName = profile?.name || 'Our Company';
    const companyLocation = profile?.address || '';
    const propertyLink = window.location.href;
    
    // Create enquiry message
    let message = `🏠 *PROPERTY ENQUIRY*\n\n`;
    message += `📋 *PROPERTY DETAILS*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `Property: *${productDetails?.name}*\n`;
    message += `Price: *${currency} ${formatPrice(productDetails?.price || 0)}*\n`;
    
    if (productDetails?.description) {
      message += `Description: ${productDetails.description}\n`;
    }
    
    if (productDetails?.brand) {
      message += `Fuel Type: ${productDetails.brand}\n`;
    }
    
    if (productDetails?.model) {
      message += `Model: ${productDetails.model}\n`;
    }
    
    if (productDetails?.warranty) {
      message += `Plate Number: ${productDetails.warranty}\n`;
    }
    
    if (productDetails?.availability) {
      message += `Available From: ${formatAvailabilityDate(productDetails.availability)}\n`;
    }
    
    message += `\n🔗 *PROPERTY LINK*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `${propertyLink}\n\n`;
    
    message += `🏢 *COMPANY INFORMATION*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `Company: ${companyName}\n`;
    if (companyLocation) {
      message += `Location: ${companyLocation}\n`;
    }
    message += `\n`;
    
    message += `❓ *ENQUIRY*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    message += `I'm interested in this property. Please provide more information about:\n`;
    message += `• Availability\n`;
    message += `• Delivery options\n`;
    message += `• Any special offers\n`;
    message += `• Payment methods\n\n`;
    
    message += `Thank you! 🙏`;
    
    // Open WhatsApp with the message
    const whatsappUrl = `https://wa.me/${profile.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  // Format availability date
  const formatAvailabilityDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString; // Return original string if parsing fails
    }
  };



  // Share handler function
  const handleShare = async () => {
    const storeName = profile?.name || 'Our Store';
    const storeLocation = profile?.address || '';
    
    const shareText = `Check out ${productDetails?.name}${productDetails?.description ? ` - ${productDetails.description}` : ''}\n\n🏪 Available at: ${storeName}${storeLocation ? `\n📍 ${storeLocation}` : ''}\n\n👉 `;
    
    const shareData = {
      title: `${productDetails?.name} | ${storeName}`,
      text: shareText,
      url: window.location.href
    };

    try {
      // Check if Web Share API is supported
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy to clipboard with formatted text
        const fullShareText = `${shareText}${window.location.href}`;
        await navigator.clipboard.writeText(fullShareText);
        setShowShareToast(true);
        setTimeout(() => setShowShareToast(false), 3000);
      }
    } catch (err) {
      // User cancelled share or error occurred
      console.log('Share failed:', err);
    }
  };

  const handleVariantSelect = (variantId, subVariantId) => {
    const variant = productDetails.ProductVariants.find(v => v.id === variantId);
    const variantName = typeof variant?.name === 'string' ? variant.name.trim().toLowerCase() : '';

    setSelectedVariants(prev => {
      // If selecting a Special variant, clear all non-Color selections
      if (variantName === 'special') {
        // Toggle off if the same special sub-variant is clicked again
        if (prev[variantId] === subVariantId) {
          const next = {};
          Object.entries(prev).forEach(([vId, svId]) => {
            const v = productDetails.ProductVariants.find(x => x.id === vId);
            const name = typeof v?.name === 'string' ? v.name.trim().toLowerCase() : '';
            // Preserve only Color/Colour selections; remove the Special selection
            if (name === 'color' || name === 'colour') {
              next[vId] = svId;
            }
          });
          return next;
        }

        const next = {};
        Object.entries(prev).forEach(([vId, svId]) => {
          const v = productDetails.ProductVariants.find(x => x.id === vId);
          const name = typeof v?.name === 'string' ? v.name.trim().toLowerCase() : '';
          if (name === 'color' || name === 'colour') {
            next[vId] = svId;
          }
        });
        next[variantId] = subVariantId;
        return next;
      }

      // If Special is currently selected and this is neither Color nor Colour, ignore selection
      if (isSpecialSelected && !(variantName === 'color' || variantName === 'colour')) {
        return prev;
      }

      return {
        ...prev,
        [variantId]: subVariantId
      };
    });

    // If the selected variant has an image, switch to it
    const subVariant = variant?.ProductSubVariants?.find(sv => sv.id === subVariantId);
    if (subVariant?.DocImage?.imageUrl) {
      setSelectedMainImage(subVariant.DocImage.imageUrl);
    }
  };

  // Calculate total price for variants with offer discount
  const calculateTotalPrice = () => {
    let basePrice = parseFloat(productDetails?.price || 0);
    
    // Add variant prices if applicable
    if (hasVariants && reorderedVariants) {
      Object.entries(selectedVariants).forEach(([variantId, subVariantId]) => {
        const variant = reorderedVariants.find(v => v.id === variantId);
        const subVariant = variant?.ProductSubVariants?.find(sv => sv.id === subVariantId);
        if (subVariant?.price) {
          basePrice += parseFloat(subVariant.price);
        }
      });
    }
    
    // Apply offer discount if available
    if (productDetails?.offer && productDetails.offer.value) {
      const discountValue = parseFloat(productDetails.offer.value);
      
      if (productDetails.offer.discount_type === 'percentage') {
        // Percentage discount
        const discountAmount = (basePrice * discountValue) / 100;
        basePrice = basePrice - discountAmount;
      } else  {
        // Fixed amount discount
        basePrice = basePrice - discountValue;
      }
      
      // Ensure price doesn't go below 0
      basePrice = Math.max(0, basePrice);
    }
    
    return basePrice;
  };

  // Helper function to format price based on offer settings
  const formatPrice = (price, isOfferPrice = false) => {
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    const formattedPrice = numericPrice.toFixed(2);
    
    // Check if this is an offer price and if enable_round is true
    if (isOfferPrice && productDetails?.offer?.enable_round === true) {
      return Math.round(numericPrice).toString();
    }
    
    // Remove .00 if it exists
    return formattedPrice.replace('.00', '');
  };

  // Check if all required variants are selected
  const areAllVariantsSelected = () => {
    if (!hasVariants || !reorderedVariants) return true;
    const getName = (v) => (typeof v?.name === 'string' ? v.name.trim().toLowerCase() : '');
    const variants = reorderedVariants;
    const specialVariant = variants.find(v => getName(v) === 'special');
    const colorVariantIds = variants.filter(v => {
      const n = getName(v);
      return n === 'color' || n === 'colour';
    }).map(v => v.id);

    if (isSpecialSelected) {
      const specialOk = specialVariant ? Boolean(selectedVariants[specialVariant.id]) : true;
      const colorOk = colorVariantIds.every(id => Boolean(selectedVariants[id]));
      return specialOk && colorOk;
    }

    // When Special is not selected, require all non-Special variants to be selected
    return variants
      .filter(v => getName(v) !== 'special')
      .every(v => Boolean(selectedVariants[v.id]));
  };

  const getAllImages = () => {
    if (!productDetails) return [];
    const images = [];
    
    // Add main product image
    if (productDetails?.DocImage?.imageUrl) {
      images.push(productDetails.DocImage.imageUrl);
    }
    
    // Add additional product images
    if (productDetails?.ProducImages) {
      productDetails.ProducImages.forEach(img => {
        if (img.DocImage?.imageUrl) {
          images.push(img.DocImage.imageUrl);
        }
      });
    }
    
    return images;
  };

  // Get variant images separately - only for "Color" variant
  const getVariantImages = () => {
    if (!productDetails?.ProductVariants) return [];
    const variantImages = [];
    
    productDetails.ProductVariants.forEach(variant => {
      // Only include images if variant name is "Color"
      if (variant.name === 'Color' && variant.ProductSubVariants) {
        variant.ProductSubVariants.forEach(subVariant => {
          if (subVariant.DocImage?.imageUrl) {
            variantImages.push({
              url: subVariant.DocImage.imageUrl,
              variantName: variant.name,
              subVariantName: subVariant.name,
              variantId: variant.id,
              subVariantId: subVariant.id
            });
          }
        });
      }
    });
    
    return variantImages;
  };

  const handleVariantImageClick = (variantImage) => {
    setSelectedMainImage(variantImage.url);
    // Also select this variant
    setSelectedVariants(prev => ({
      ...prev,
      [variantImage.variantId]: variantImage.subVariantId
    }));
  };

  const handleNextImage = () => {
    const images = getAllImages();
    const nextIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(nextIndex);
    setSelectedMainImage(images[nextIndex]);
  };

  const handlePrevImage = () => {
    const images = getAllImages();
    const prevIndex = currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedMainImage(images[prevIndex]);
  };

  const handleImageSelect = (index) => {
    const images = getAllImages();
    setCurrentImageIndex(index);
    setSelectedMainImage(images[index]);
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextImage();
    }
    if (isRightSwipe) {
      handlePrevImage();
    }
  };

  // Mouse handlers for drag
  const handleMouseDown = (e) => {
    setTouchEnd(null);
    setTouchStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (touchStart === null) return;
    setTouchEnd(e.clientX);
  };

  const handleMouseUp = () => {
    if (!touchStart || !touchEnd) {
      setTouchStart(null);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextImage();
    }
    if (isRightSwipe) {
      handlePrevImage();
    }
    
    setTouchStart(null);
  };

  const handleMouseLeave = () => {
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Video navigation handlers
  const getVideos = () => {
    if (!productDetails?.ProductVideos || productDetails.ProductVideos.length === 0) return [];
    return productDetails.ProductVideos.filter(video => video.link);
  };

  const handleNextVideo = () => {
    const videos = getVideos();
    if (videos.length === 0) return;
    const nextIndex = (currentVideoIndex + 1) % videos.length;
    setCurrentVideoIndex(nextIndex);
  };

  const handlePrevVideo = () => {
    const videos = getVideos();
    if (videos.length === 0) return;
    const prevIndex = currentVideoIndex === 0 ? videos.length - 1 : currentVideoIndex - 1;
    setCurrentVideoIndex(prevIndex);
  };

  const handleVideoSelect = (index) => {
    setCurrentVideoIndex(index);
  };

  // Video touch handlers for swipe
  const handleVideoTouchStart = (e) => {
    setVideoTouchEnd(null);
    setVideoTouchStart(e.targetTouches[0].clientX);
  };

  const handleVideoTouchMove = (e) => {
    setVideoTouchEnd(e.targetTouches[0].clientX);
  };

  const handleVideoTouchEnd = () => {
    if (!videoTouchStart || !videoTouchEnd) return;
    
    const distance = videoTouchStart - videoTouchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextVideo();
    }
    if (isRightSwipe) {
      handlePrevVideo();
    }
  };

  // Video mouse handlers for drag
  const handleVideoMouseDown = (e) => {
    setVideoTouchEnd(null);
    setVideoTouchStart(e.clientX);
  };

  const handleVideoMouseMove = (e) => {
    if (videoTouchStart === null) return;
    setVideoTouchEnd(e.clientX);
  };

  const handleVideoMouseUp = () => {
    if (!videoTouchStart || !videoTouchEnd) {
      setVideoTouchStart(null);
      return;
    }
    
    const distance = videoTouchStart - videoTouchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextVideo();
    }
    if (isRightSwipe) {
      handlePrevVideo();
    }
    
    setVideoTouchStart(null);
  };

  const handleVideoMouseLeave = () => {
    setVideoTouchStart(null);
    setVideoTouchEnd(null);
  };

  // Reset video index when switching tabs
  useEffect(() => {
    if (activeTab === 'video') {
      setCurrentVideoIndex(0);
    }
  }, [activeTab]);

  // Auto-scroll image preview to selected thumbnail
  useEffect(() => {
    const allImages = getAllImages();
    if (imagePreviewRef.current && allImages.length > 1) {
      const thumbnailWidth = 80; // w-20 = 80px on mobile
      const gap = 8; // gap-2 = 8px
      const scrollPosition = currentImageIndex * (thumbnailWidth + gap) - (imagePreviewRef.current.offsetWidth / 2) + (thumbnailWidth / 2);
      imagePreviewRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentImageIndex, productDetails]);

  // Auto-scroll video preview to selected thumbnail
  useEffect(() => {
    const videos = getVideos();
    if (videoPreviewRef.current && videos.length > 1) {
      const thumbnailWidth = 80; // w-20 = 80px on mobile
      const gap = 8; // gap-2 = 8px
      const scrollPosition = currentVideoIndex * (thumbnailWidth + gap) - (videoPreviewRef.current.offsetWidth / 2) + (thumbnailWidth / 2);
      videoPreviewRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentVideoIndex, productDetails?.ProductVideos]);

  if (productDetailsStatus === 'loading') {
    return (
      <main className="min-h-screen bg-gray-50">
        <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
          <Back />
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading product details...</p>
          </div>
        </div>
      </main>
    );
  }

  if (productDetailsStatus === 'failed' || !productDetails) {
    return (
      <main className="min-h-screen bg-gray-50">
        <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
          <Back />
          <div className="text-center py-12">
            <div className="text-6xl mb-4">😔</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Product Not Found</h2>
            <p className="text-gray-600">{error || 'Unable to load product details'}</p>
          </div>
        </div>
      </main>
    );
  }

  const currency = profile?.currency || "AED";
  const hasVariants = productDetails.ProductVariants && 
    productDetails.ProductVariants.length > 0 && 
    productDetails.ProductVariants.some(variant => 
      variant.ProductSubVariants && variant.ProductSubVariants.length > 0
    );
  const allImages = getAllImages();

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-screen-xl pt-20 md:pt-24 lg:pt-32 pb-24 md:pb-20 lg:pb-32 mx-auto px-3 md:px-4 lg:px-6">
        <Back />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Images/Videos */}
          <div className="space-y-4">
            {/* Tabs for Image/Video */}
            {(productDetails?.ProductVideos && productDetails.ProductVideos.length > 0) && (
              <div className="flex gap-2 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('image')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'image'
                      ? 'border-b-2 border-black text-black'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Images
                </button>
                <button
                  onClick={() => setActiveTab('video')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'video'
                      ? 'border-b-2 border-black text-black'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Videos
                </button>
              </div>
            )}

            {/* Image Tab Content */}
            {activeTab === 'image' && (
              <>
                {/* Main Image with Swiper */}
                <div 
                  className="bg-white rounded-xl overflow-hidden relative select-none cursor-grab active:cursor-grabbing"
                  style={{
                    boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)'
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseLeave}
                >
                  <img
                    src={selectedMainImage || productDetails?.DocImage?.imageUrl}
                    alt={productDetails?.name}
                    className="w-full h-96 object-contain bg-white pointer-events-none"
                  />
                  
                  {/* Swiper Navigation - Only show if multiple images */}
                  {allImages.length > 1 && (
                    <>
                      {/* Previous Button */}
                      <button
                        onClick={handlePrevImage}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-black rounded-full p-3 transition-all shadow-lg"
                      >
                        <FaChevronLeft className="w-4 h-4" />
                      </button>
                      
                      {/* Next Button */}
                      <button
                        onClick={handleNextImage}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-black rounded-full p-3 transition-all shadow-lg"
                      >
                        <FaChevronRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>

                {/* Image Preview Thumbnails - Horizontal Scrollable */}
                {allImages.length > 1 && (
                  <div 
                    ref={imagePreviewRef}
                    className="flex gap-2 overflow-x-auto scrollbar-hide pb-2"
                    style={{
                      scrollBehavior: 'smooth',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    {allImages.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => handleImageSelect(index)}
                        className={`flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                          currentImageIndex === index
                            ? 'border-black scale-105 shadow-lg'
                            : 'border-gray-200 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={img}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {/* Variant Images - Selectable Thumbnails */}
                {getVariantImages().length > 0 && (
                  <div>
                    <div className="grid grid-cols-4 gap-2">
                      {getVariantImages().map((variantImg, index) => (
                        <button
                          key={index}
                          onClick={() => handleVariantImageClick(variantImg)}
                          className={`bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all ${
                            selectedMainImage === variantImg.url ? 'ring-2 ring-black scale-105' : ''
                          }`}
                        >
                          <img
                            src={variantImg.url}
                            alt={`${variantImg.variantName} - ${variantImg.subVariantName}`}
                            className="w-full h-20 object-contain bg-gray-50"
                          />
                          <div className="px-1 py-1 bg-gray-50 text-center">
                            <p className="text-xs font-medium text-gray-700 truncate">
                              {variantImg.subVariantName}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Video Tab Content */}
            {activeTab === 'video' && (() => {
              const videos = getVideos();
              if (videos.length === 0) return null;

              // Convert YouTube URL to embed format
              const getEmbedUrl = (url) => {
                if (!url) return '';
                // Handle YouTube shorts and regular videos
                let videoId = '';
                if (url.includes('youtube.com/shorts/')) {
                  videoId = url.split('youtube.com/shorts/')[1]?.split('?')[0];
                } else if (url.includes('youtube.com/watch?v=')) {
                  videoId = url.split('v=')[1]?.split('&')[0];
                } else if (url.includes('youtu.be/')) {
                  videoId = url.split('youtu.be/')[1]?.split('?')[0];
                }
                return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
              };

              const currentVideo = videos[currentVideoIndex];
              const embedUrl = currentVideo ? getEmbedUrl(currentVideo.link) : '';

              if (!embedUrl) return null;

              return (
                <>
                  {/* Main Video with Swiper */}
                  <div 
                    className="bg-white rounded-xl overflow-hidden relative select-none cursor-grab active:cursor-grabbing"
                    style={{
                      boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)'
                    }}
                    onTouchStart={handleVideoTouchStart}
                    onTouchMove={handleVideoTouchMove}
                    onTouchEnd={handleVideoTouchEnd}
                    onMouseDown={handleVideoMouseDown}
                    onMouseMove={handleVideoMouseMove}
                    onMouseUp={handleVideoMouseUp}
                    onMouseLeave={handleVideoMouseLeave}
                  >
                    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}> {/* 16:9 aspect ratio */}
                      <iframe
                        src={embedUrl}
                        className="absolute top-0 left-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={`Product Video ${currentVideoIndex + 1}`}
                      />
                    </div>
                    
                    {/* Swiper Navigation - Only show if multiple videos */}
                    {videos.length > 1 && (
                      <>
                        {/* Previous Button */}
                        <button
                          onClick={handlePrevVideo}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-black rounded-full p-3 transition-all shadow-lg z-10"
                        >
                          <FaChevronLeft className="w-4 h-4" />
                        </button>
                        
                        {/* Next Button */}
                        <button
                          onClick={handleNextVideo}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 text-black rounded-full p-3 transition-all shadow-lg z-10"
                        >
                          <FaChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Video Preview Thumbnails - Horizontal Scrollable */}
                  {videos.length > 1 && (() => {
                    // Convert YouTube URL to embed format for thumbnail
                    const getThumbnailUrl = (url) => {
                      if (!url) return '';
                      let videoId = '';
                      if (url.includes('youtube.com/shorts/')) {
                        videoId = url.split('youtube.com/shorts/')[1]?.split('?')[0];
                      } else if (url.includes('youtube.com/watch?v=')) {
                        videoId = url.split('v=')[1]?.split('&')[0];
                      } else if (url.includes('youtu.be/')) {
                        videoId = url.split('youtu.be/')[1]?.split('?')[0];
                      }
                      return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
                    };

                    return (
                      <div 
                        ref={videoPreviewRef}
                        className="flex gap-2 overflow-x-auto scrollbar-hide pb-2"
                        style={{
                          scrollBehavior: 'smooth',
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          WebkitOverflowScrolling: 'touch'
                        }}
                      >
                        {videos.map((video, index) => {
                          const thumbnailUrl = getThumbnailUrl(video.link);
                          return (
                            <button
                              key={index}
                              onClick={() => handleVideoSelect(index)}
                              className={`relative flex-shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                                currentVideoIndex === index
                                  ? 'border-black scale-105 shadow-lg'
                                  : 'border-gray-200 hover:border-gray-400'
                              }`}
                            >
                              {thumbnailUrl ? (
                                <>
                                  <img
                                    src={thumbnailUrl}
                                    alt={`Video thumbnail ${index + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                  {/* Play button overlay */}
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                    <div className="w-8 h-8 md:w-10 md:h-10 bg-white/90 rounded-full flex items-center justify-center">
                                      <FaPlay className="text-black text-xs md:text-sm ml-0.5" />
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                  <FaPlay className="text-gray-400 text-2xl" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              );
            })()}
          </div>

          {/* Right Column - Product Info */}
          <div className="space-y-4">
            {/* Product Title & Category */}
            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {productDetails?.name}
                  </h1>
                  {productDetails?.ProductCategory && (
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
                      {productDetails.ProductCategory.name}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-3 ml-4">
                  {/* Map Button */}
                  {productDetails?.map_url && (
                    <button
                      onClick={handleMapClick}
                      className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10"
                      style={{
                        backgroundColor: '#34a853', // Google green background
                        color: '#ffffff', // White icon color
                        border: '2px solid #34a853' // Google green border
                      }}
                      title="View Location on Map"
                    >
                      <FaMapMarkerAlt className="text-lg" />
                    </button>
                  )}
                  
                  {/* Share Button */}
                  <button
                    onClick={handleShare}
                    className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-10"
                    style={{
                      backgroundColor: theme.colors.productAddButtonBg,
                      color: theme.colors.productAddButtonText,
                      border: `2px solid ${theme.colors.productAddButtonBorder}`
                    }}
                    title="Share Product"
                  >
                    <FaShareAlt className="text-lg" />
                  </button>
                  
                  {/* Veg/Non-Veg Icon */}
                  {productDetails?.is_veg !== undefined && (
                    <div className={`flex-shrink-0 w-6 h-6 border-2 flex items-center justify-center ${
                      productDetails.is_veg ? 'border-green-600' : 'border-red-600'
                    }`}>
                      <div className={`w-3 h-3 rounded-full ${
                        productDetails.is_veg ? 'bg-green-600' : 'bg-red-600'
                      }`} />
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {productDetails?.description && (
                <p className="text-gray-600 text-base leading-relaxed mb-4">
                  {productDetails.description}
                </p>
              )}

              {/* Brand, Model, Plate Number, Availability */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                {productDetails?.brand && (
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <FaGasPump className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Fuel Type</p>
                      <p className="text-sm font-semibold text-gray-900">{productDetails.brand}</p>
                    </div>
                  </div>
                )}
                {productDetails?.model && (
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <FaCog className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Model</p>
                      <p className="text-sm font-semibold text-gray-900">{productDetails.model}</p>
                    </div>
                  </div>
                )}
                {productDetails?.warranty && (
                  <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-3">
                    <FaIdCard className="text-gray-500" />
                    <div>
                      <p className="text-xs text-gray-500">Plate Number</p>
                      <p className="text-sm font-semibold text-gray-900">{productDetails.warranty}</p>
                    </div>
                  </div>
                )}
                {productDetails?.availability && (
                  <div className="flex items-center space-x-2 bg-green-50 rounded-lg p-3 border border-green-200 col-span-1 sm:col-span-2 lg:col-span-2">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 text-xs font-bold">📅</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-green-600 font-medium whitespace-nowrap">Available From</p>
                      <p className="text-sm font-semibold text-green-800 whitespace-nowrap truncate">{formatAvailabilityDate(productDetails.availability)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Base Price */}
              <div className="mb-4">
                <div className="flex items-baseline space-x-3">
                  <span className="text-sm text-gray-500">Base Price:</span>
                  <span className="text-xl font-bold text-gray-900">
                    {currency} {formatPrice(productDetails.price)}
                  </span>
                  {productDetails?.offer && productDetails.offer.badge && (
                    <span className="text-xs font-semibold px-2 py-1 bg-red-100 text-red-600 rounded-full">
                      {productDetails.offer.badge}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Variants Section */}
            {hasVariants && (
              <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Select Options</h2>
                
                {reorderedVariants.map((variant, variantIndex) => (
                  <div key={variant.id} className="mb-6 last:mb-0">
                    {!(typeof variant?.name === 'string' && variant.name.trim().toLowerCase() === 'special') && 
                     variant.ProductSubVariants && variant.ProductSubVariants.length > 0 && (
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">
                        {variant.name}
                      </h3>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {variant.ProductSubVariants?.map((subVariant) => {
                        const isSelected = selectedVariants[variant.id] === subVariant.id;
                        const variantName = typeof variant?.name === 'string' ? variant.name.trim().toLowerCase() : '';
                        const isColorVariant = variantName === 'color' || variantName === 'colour';
                        const isSpecialVariant = variantName === 'special';
                        const isDisabled = isSpecialSelected && !isColorVariant && !isSpecialVariant;
                        
                        // Calculate base price including all previously selected variants
                        let basePrice = parseFloat(productDetails.price);
                        reorderedVariants.forEach((v, idx) => {
                          // Only add prices from variants that come before this one
                          if (idx < variantIndex && selectedVariants[v.id]) {
                            const selectedSubVar = v.ProductSubVariants?.find(sv => sv.id === selectedVariants[v.id]);
                            if (selectedSubVar?.price) {
                              basePrice += parseFloat(selectedSubVar.price);
                            }
                          }
                        });
                        
                        const totalPriceForVariant = basePrice + parseFloat(subVariant.price);
                        
                        return (
                          <button
                            key={subVariant.id}
                            onClick={() => handleVariantSelect(variant.id, subVariant.id)}
                            disabled={isDisabled}
                            className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                              isSelected
                                ? 'border-black bg-white shadow-xl scale-110 z-10'
                                : `border-gray-200 ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-gray-300'} scale-95 opacity-80`
                            }`}
                          >
                            <div className={`font-semibold text-gray-900 ${isSelected ? 'text-base' : 'text-sm'}`}>
                              {subVariant.name}
                            </div>
                            <div className={`font-bold mt-1 text-green-700 ${isSelected ? 'text-sm' : 'text-xs'}`}>
                              {currency} {formatPrice(totalPriceForVariant)}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Total Price and Add to Cart */}
                <div className="mt-6 pt-6 border-t border-gray-200">

                  {/* Show price breakdown if offer exists */}
                  {productDetails?.offer && productDetails.offer.value ? (
                    <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>Subtotal (before offer):</span>
                        <span>
                          {currency} {(() => {
                            let basePrice = parseFloat(productDetails.price);
                            if (hasVariants && productDetails.ProductVariants) {
                              Object.entries(selectedVariants).forEach(([variantId, subVariantId]) => {
                                const variant = productDetails.ProductVariants.find(v => v.id === variantId);
                                const subVariant = variant?.ProductSubVariants?.find(sv => sv.id === subVariantId);
                                if (subVariant?.price) {
                                  basePrice += parseFloat(subVariant.price);
                                }
                              });
                            }
                            return formatPrice(basePrice);
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm font-semibold text-green-700 mb-2">
                        <span>Offer Discount ({productDetails.offer.badge}):</span>
                        <span>
                          - {currency} {(() => {
                            let basePrice = parseFloat(productDetails.price);
                            if (hasVariants && productDetails.ProductVariants) {
                              Object.entries(selectedVariants).forEach(([variantId, subVariantId]) => {
                                const variant = productDetails.ProductVariants.find(v => v.id === variantId);
                                const subVariant = variant?.ProductSubVariants?.find(sv => sv.id === subVariantId);
                                if (subVariant?.price) {
                                  basePrice += parseFloat(subVariant.price);
                                }
                              });
                            }
                            const discountValue = parseFloat(productDetails.offer.value);
                            let discount = 0;
                            if (productDetails.offer.discount_type === 'percentage') {
                              discount = (basePrice * discountValue) / 100;
                            } else {
                              discount = discountValue;
                            }
                            return formatPrice(discount);
                          })()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-green-300">
                        <span className="text-lg font-bold text-gray-900">Total Price:</span>
                        <span className="text-2xl font-bold" style={{ color: theme.colors.offerPriceText }}>
                          {currency} {formatPrice(calculateTotalPrice())}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-lg font-semibold text-gray-700">Total Price:</span>
                      <span className="text-2xl font-bold text-gray-900">
                        {currency} {formatPrice(calculateTotalPrice())}
                      </span>
                    </div>
                  )}

                  {/* Enquiry Button and Favorite Button */}
                  {!areAllVariantsSelected() ? (
                    <div className="text-center py-3 px-4 bg-yellow-50 text-yellow-800 rounded-lg text-sm">
                      Please select all options to continue
                    </div>
                  ) : getCartQuantity() === 0 ? (
                    <div className="flex gap-3">
                      <button
                        onClick={handleWhatsAppEnquiry}
                        className="flex-1 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
                        style={{
                          backgroundColor: theme.colors.productAddButtonBg,
                          color: theme.colors.productAddButtonText,
                          border: `2px solid ${theme.colors.productAddButtonBorder}`
                        }}
                      >
                        <FaWhatsapp className="inline mr-2" />
                        Enquiry
                      </button>
                      <button
                        onClick={handleFavoriteToggle}
                        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                        style={{
                          backgroundColor: isFavorited ? '#ec4899' : 'rgba(255, 255, 255, 0.9)',
                          color: isFavorited ? 'white' : theme.colors.primary,
                          border: `2px solid ${isFavorited ? '#ec4899' : 'rgba(0, 0, 0, 0.1)'}`
                        }}
                        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {isFavorited ? <HiHeart className="text-2xl" /> : <HiOutlineHeart className="text-2xl" />}
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-4 bg-gray-50 rounded-full px-6 py-3 shadow-md">
                      <button
                        onClick={() => updateQuantity(getCartQuantity() - 1)}
                        className="w-10 h-10 bg-white text-gray-700 rounded-full flex items-center justify-center text-xl font-bold shadow-sm border border-gray-200 hover:bg-gray-100 transition-all"
                      >
                        -
                      </button>
                      <span className="text-xl font-bold text-gray-800 min-w-[40px] text-center">
                        {getCartQuantity()}
                      </span>
                      <button
                        onClick={() => updateQuantity(getCartQuantity() + 1)}
                        className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold shadow-sm transition-all"
                        style={{
                          backgroundColor: theme.colors.productAddButtonBg,
                          color: theme.colors.productAddButtonText,
                          border: `2px solid ${theme.colors.productAddButtonBorder}`
                        }}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Add to Cart for non-variant products */}
            {!hasVariants && (
              <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">

                {/* Total Price */}
                {productDetails?.offer && productDetails.offer.value ? (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                      <span>Subtotal (before offer):</span>
                      <span>
                        {currency} {formatPrice(productDetails.price)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm font-semibold text-green-700 mb-2">
                      <span>Offer Discount ({productDetails.offer.badge}):</span>
                      <span>
                        - {currency} {(() => {
                          const basePrice = parseFloat(productDetails.price);
                          const discountValue = parseFloat(productDetails.offer.value);
                          let discount = 0;
                          if (productDetails.offer.discount_type === 'percentage') {
                            discount = (basePrice * discountValue) / 100;
                          } else {
                            discount = discountValue;
                          }
                          return formatPrice(discount);
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-green-300">
                      <span className="text-lg font-bold text-gray-900">Total Price:</span>
                      <span className="text-2xl font-bold" style={{ color: theme.colors.offerPriceText }}>
                        {currency} {formatPrice(calculateTotalPrice())}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-lg font-semibold text-gray-700">Total Price:</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {currency} {formatPrice(calculateTotalPrice())}
                    </span>
                  </div>
                )}

                {getCartQuantity() === 0 ? (
                  <div className="flex gap-3">
                    <button
                      onClick={handleWhatsAppEnquiry}
                      className="flex-1 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
                      style={{
                        backgroundColor: theme.colors.productAddButtonBg,
                        color: theme.colors.productAddButtonText,
                        border: `2px solid ${theme.colors.productAddButtonBorder}`
                      }}
                    >
                      <FaWhatsapp className="inline mr-2" />
                      Enquiry
                    </button>
                    <button
                      onClick={handleFavoriteToggle}
                      className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                      style={{
                        backgroundColor: isFavorited ? '#ec4899' : 'rgba(255, 255, 255, 0.9)',
                        color: isFavorited ? 'white' : theme.colors.primary,
                        border: `2px solid ${isFavorited ? '#ec4899' : 'rgba(0, 0, 0, 0.1)'}`
                      }}
                      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {isFavorited ? <HiHeart className="text-2xl" /> : <HiOutlineHeart className="text-2xl" />}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-4 bg-gray-50 rounded-full px-6 py-3 shadow-md">
                    <button
                      onClick={() => updateQuantity(getCartQuantity() - 1)}
                      className="w-10 h-10 bg-white text-gray-700 rounded-full flex items-center justify-center text-xl font-bold shadow-sm border border-gray-200 hover:bg-gray-100 transition-all"
                    >
                      -
                    </button>
                    <span className="text-xl font-bold text-gray-800 min-w-[40px] text-center">
                      {getCartQuantity()}
                    </span>
                    <button
                      onClick={() => updateQuantity(getCartQuantity() + 1)}
                      className="w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold shadow-sm transition-all"
                      style={{
                        backgroundColor: theme.colors.productAddButtonBg,
                        color: theme.colors.productAddButtonText,
                        border: `2px solid ${theme.colors.productAddButtonBorder}`
                      }}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Features */}
            {productDetails?.ProductFeatures && productDetails.ProductFeatures.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Key Features</h2>
                <ul className="space-y-2">
                  {productDetails.ProductFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{feature.feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Specifications */}
            {productDetails?.ProductSpecifications && productDetails.ProductSpecifications.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-4 md:p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Specifications</h2>
                <div className="divide-y divide-gray-200">
                  {productDetails.ProductSpecifications.map((spec, index) => (
                    <div key={index} className="py-3 flex justify-between">
                      <span className="text-gray-600 font-medium">{spec.key}</span>
                      <span className="text-gray-900 font-semibold">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Share Toast Notification */}
      {showShareToast && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-full shadow-lg flex items-center space-x-2">
            <FaCheckCircle className="text-green-400" />
            <span>Link copied to clipboard!</span>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProductDetailsScreen;
